import { APP_NAME, APP_TAGLINE, BRAND_LOGO, BRAND_SKY } from "@/lib/brand";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle2, KeyRound } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="reset-password-page">
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(37,99,235,0.88), rgba(59,130,246,0.42)), url(${BRAND_SKY})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 inline-flex items-center bg-white/95 rounded-xl px-3.5 py-2 shadow-sm w-fit">
          <img src={BRAND_LOGO} alt={APP_NAME} className="h-[31px] w-auto object-contain" />
        </div>
        <div className="relative z-10 fade-in-up">
          <p className="overline text-white/80">Account security</p>
          <h1 className="mt-4 text-4xl xl:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-md">{APP_TAGLINE}</h1>
        </div>
        <div className="relative z-10 text-xs text-white/70">© {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6"><img src={BRAND_LOGO} alt={APP_NAME} className="h-8 w-auto object-contain" /></div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Shell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">Invalid reset link</h1>
          <p className="mt-2 text-sm text-slate-500">This link is missing its token. Request a new one from the sign-in page.</p>
          <Button variant="outline" className="mt-6" onClick={() => navigate("/login")} data-testid="reset-back-login">Back to sign in</Button>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="reset-done">
          <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 grid place-items-center"><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
          <h1 className="mt-4 text-xl font-bold text-slate-900">Password updated</h1>
          <p className="mt-2 text-sm text-slate-500">You can now sign in with your new password.</p>
          <Button className="mt-6 bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={() => navigate("/login")} data-testid="reset-go-login">Go to sign in</Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="overline">Reset password</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Choose a new password</h1>
      <p className="mt-2 text-sm text-slate-500">Enter a new password for your account.</p>
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div>
          <Label className="text-xs font-medium text-slate-600">New password (min 8 characters)</Label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" className="mt-1" data-testid="reset-new-password" />
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="mt-1" data-testid="reset-confirm-password" />
        </div>
        <Button onClick={submit} disabled={submitting || !pw || !confirm} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-10" data-testid="reset-submit">
          <KeyRound className="mr-2 h-4 w-4" />{submitting ? "Updating…" : "Update password"}
          {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </Shell>
  );
}
